/**
 * ShareSheet — the way out of Rammy.
 *
 * Honest about what's wired: download and a public link work for everything;
 * YouTube is a real upload to the user's own channel (Shorts when it's
 * vertical and short). Instagram, TikTok, X and LinkedIn are shown as not yet
 * wired rather than as buttons that quietly do nothing.
 */
import { useEffect, useRef, useState } from "react";
import * as L from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const ICONS = {
  youtube: L.Youtube,
  instagram: L.Instagram,
  tiktok: L.Music2,
  x: L.Twitter,
  linkedin: L.Linkedin,
};

export function ShareSheet({ creation, onClose }) {
  const [accounts, setAccounts] = useState([]);
  const [link, setLink] = useState(null);
  const [linking, setLinking] = useState(false);
  const [title, setTitle] = useState((creation?.title || "").slice(0, 100));
  const [description, setDescription] = useState("");
  const [privacy, setPrivacy] = useState("unlisted");
  const [post, setPost] = useState(null);
  const pollRef = useRef(null);

  const isVideo = creation?.kind === "video";
  const yt = accounts.find((a) => a.id === "youtube");

  useEffect(() => {
    api.get("/social/accounts").then((r) => setAccounts(r.data?.accounts || [])).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, []);

  const makeLink = async () => {
    setLinking(true);
    try {
      const { data } = await api.post("/social/share-link", { creation_id: creation.id });
      setLink(data.url);
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success("Link copied — anyone with it can open this");
      } catch (_) {
        toast.success("Link ready");
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLinking(false);
    }
  };

  const download = async () => {
    try {
      const res = await api.get(`/studio/creations/${creation.id}/file`, {
        responseType: "blob", timeout: 180000,
      });
      const url = URL.createObjectURL(res.data);
      const ext = creation.kind === "video" ? "mp4" : creation.kind === "image" ? "png" : "mp3";
      const slug = (creation.title || "rammy").toLowerCase()
        .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "rammy";
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 8000);
    } catch (_) {
      toast.error("Couldn't download that");
    }
  };

  const connectGoogle = async () => {
    try {
      const { data } = await api.get("/oauth/calendar/authorize");
      window.location.href = data.authorize_url;
    } catch (_) {
      toast.error("Couldn't start the Google connection");
    }
  };

  const uploadYouTube = async () => {
    if (!title.trim()) { toast.error("Give it a title first"); return; }
    try {
      const { data } = await api.post("/social/youtube/upload", {
        creation_id: creation.id,
        title: title.trim(),
        description,
        privacy,
        shorts: true,
      });
      setPost(data);
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/social/posts/${data.id}`);
          setPost(r.data);
          if (r.data.status === "done" || r.data.status === "error") {
            clearInterval(pollRef.current);
            if (r.data.status === "done") toast.success("It's on your channel");
            else toast.error(r.data.error || "YouTube wouldn't take it");
          }
        } catch (_) { /* a blip shouldn't kill the poll */ }
      }, 4000);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(formatApiError(d));
      if (d?.error === "needs_reconnect" || d?.error === "not_connected") connectGoogle();
    }
  };

  const busy = post && (post.status === "queued" || post.status === "uploading");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45 backdrop-blur-sm"
      onClick={onClose}
      data-testid="share-sheet"
    >
      <div
        className="w-full max-w-lg max-h-[88vh] overflow-y-auto subtle-scroll rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-6 shadow-[0_24px_60px_rgba(17,19,24,0.28)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-2xl">Share it</h2>
            <p className="text-sm text-[var(--rammy-text-sec)] mt-1 truncate">
              {creation?.title || "Untitled"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-sec)]"
            data-testid="share-close"
            aria-label="Close"
          >
            <L.X size={16} />
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2">
          <Button
            onClick={download}
            variant="outline"
            className="btn-pill h-10 justify-center"
            data-testid="share-download"
          >
            <L.Download size={14} className="mr-1.5" /> Download
          </Button>
          <Button
            onClick={makeLink}
            variant="outline"
            className="btn-pill h-10 justify-center"
            disabled={linking}
            data-testid="share-copy-link"
          >
            {linking
              ? <L.Loader2 size={14} className="mr-1.5 animate-spin" />
              : <L.Link2 size={14} className="mr-1.5" />}
            Copy link
          </Button>
        </div>

        {link && (
          <div
            className="mt-2 rounded-xl bg-[var(--rammy-surface-alt)] px-3 py-2 font-mono text-[11px] break-all text-[var(--rammy-text-sec)]"
            data-testid="share-link-value"
          >
            {link}
          </div>
        )}

        <div className="mt-6 pt-5 border-t border-[var(--rammy-border)]">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">
            Straight to a channel
          </div>

          {/* YouTube */}
          <div className="mt-3 rounded-2xl border border-[var(--rammy-border)] p-4" data-testid="share-youtube">
            <div className="flex items-center gap-2.5">
              <L.Youtube size={18} className="text-[#FF0033]" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">YouTube</div>
                <div className="text-[11.5px] text-[var(--rammy-text-muted)] truncate">
                  {yt?.connected ? (yt.account || "Connected") : yt?.hint || "Connect Google to upload"}
                </div>
              </div>
              {!yt?.connected && (
                <Button
                  onClick={connectGoogle}
                  className="btn-pill h-8 text-xs"
                  data-testid="share-youtube-connect"
                >
                  {yt?.needs_reconnect ? "Reconnect" : "Connect"}
                </Button>
              )}
            </div>

            {!isVideo && (
              <p className="mt-3 text-[12px] text-[var(--rammy-text-muted)]">
                YouTube takes video only — make this into a video first and it'll show up here.
              </p>
            )}

            {yt?.connected && isVideo && post?.status !== "done" && (
              <div className="mt-4 space-y-2.5">
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={100}
                  placeholder="Title"
                  className="rounded-xl"
                  data-testid="share-youtube-title"
                />
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  maxLength={4000}
                  placeholder="Description (optional) — #Shorts is added for you"
                  className="rounded-xl resize-y"
                  data-testid="share-youtube-description"
                />
                <div className="flex items-center gap-1.5" data-testid="share-youtube-privacy">
                  {["public", "unlisted", "private"].map((p) => (
                    <button
                      key={p}
                      onClick={() => setPrivacy(p)}
                      aria-pressed={privacy === p}
                      className={`px-3 py-1.5 rounded-full text-[11.5px] border capitalize transition-colors ${
                        privacy === p
                          ? "bg-[var(--rm-ink-900)] text-white border-[var(--rm-ink-900)]"
                          : "bg-[var(--rm-surface)] border-[var(--rammy-border)] text-[var(--rammy-text-sec)] hover:border-[var(--rammy-cyan)]"
                      }`}
                      data-testid={`share-youtube-privacy-${p}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <Button
                  onClick={uploadYouTube}
                  disabled={busy}
                  className="btn-pill btn-primary h-10 w-full justify-center"
                  data-testid="share-youtube-upload"
                >
                  {busy
                    ? <><L.Loader2 size={14} className="mr-1.5 animate-spin" /> Uploading…</>
                    : <><L.Upload size={14} className="mr-1.5" /> Upload to YouTube</>}
                </Button>
              </div>
            )}

            {post?.status === "done" && post.url && (
              <a
                href={post.url}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 text-[13px] text-[var(--rammy-cyan-dark)] hover:underline"
                data-testid="share-youtube-result"
              >
                <L.ExternalLink size={13} /> It's live — open it on YouTube
              </a>
            )}
          </div>

          {/* Not yet wired */}
          <div className="mt-2 grid grid-cols-2 gap-2" data-testid="share-pending">
            {accounts.filter((a) => !a.available).map((a) => {
              const Icon = ICONS[a.id] || L.Share2;
              return (
                <div
                  key={a.id}
                  className="rounded-2xl border border-dashed border-[var(--rammy-border)] p-3 opacity-70"
                  title={a.hint}
                  data-testid={`share-pending-${a.id}`}
                >
                  <div className="flex items-center gap-2">
                    <Icon size={15} className="text-[var(--rammy-text-muted)]" />
                    <span className="text-[12.5px] font-medium">{a.label}</span>
                  </div>
                  <div className="text-[10.5px] text-[var(--rammy-text-muted)] mt-1 leading-snug">
                    {a.hint}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShareSheet;
